import { token } from '@atlaskit/tokens';
import Select, {
  type FormatOptionLabelMeta,
  type StylesConfig,
} from '@databases/atlaskit/Select';
import BrokenLink from '@databases/content-link/BrokenLink';
import EntryLink from '@databases/content-link/EntryLink';
import EntryCoverImage from '@databases/cover-image/EntryCoverImage';
// FIXME circular dependency
// eslint-disable-next-line workspaces/require-dependency
import { getSelectDatabaseDraft } from '@databases/fields/utils';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils, useActionWithDependencies } from '@databases/redux';
import { type Database, type EntryWithContext } from '@databases/types/database';
import { loadDependencies } from '@databases/utils/database/field-types';
import {
  getCoverField,
  getDatabaseEntries,
  getDatabaseValue,
  getEntryName,
  getFirstField,
} from '@databases/utils/database/utils';
import React, { type ReactElement, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${token('space.050')};
`;

function validateEntry(
  value: Array<EntryWithContext>,
  isRequired: boolean | undefined,
  validationMessage: string
): string | undefined {
  if (isRequired && value.length === 0) {
    return validationMessage;
  }
}

const getOptionValue = (value: EntryWithContext) => (value.entry ? value.entry.id : '');

const styles: StylesConfig<EntryWithContext> = {
  menu: (base) => ({ ...base, minWidth: '100%' }),
  singleValue: (base) => ({
    ...base,
    padding: token('space.025', '2px'), // Give space to preview box shadow
    // @remark: Disabled as part of https://bitbucket.org/atlassian/canvas/pull-requests/27504/overview,
    // this should be re-enabled as soon as feasible
    // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
    lineHeight: '20px',
    pointerEvents: 'none', // Prevent interaction with preview
  }),
};

export interface MultiEntryPickerProps {
  value: Array<string>;
  database: Database | undefined;
  onChange: (value: Array<string>) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isClearable?: boolean;
  isRequired?: boolean;
  isLoading?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  noOptionsMessage?: string;
  isMulti?: boolean;
  isSeamless?: boolean;

  /** If true, render the selected value as an entry link rather than a simple text title. */
  isLinkPreview?: boolean;
}

/**
 * Renders a select box that allows selecting one or multiple (depending on isMulti) specific database entries from the given database.
 */
export function MultiEntryPicker({
  value,
  database,
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  isClearable,
  isRequired,
  isLoading,
  isInvalid,
  isDisabled,
  noOptionsMessage,
  isMulti,
  isSeamless,
  isLinkPreview,
}: MultiEntryPickerProps): ReactElement {
  const store = defaultStoreUtils.useStore();
  const intl = useIntl();
  const { formatMessage } = intl;
  const dependencies = useActionWithDependencies(
    useCallback(
      (helpers) => {
        // Load first field dependencies (because the picker item label is the string representation of the main field)
        const firstField = database && getFirstField(database);
        if (firstField) {
          for (const entry of getDatabaseEntries(database)) {
            const value = getDatabaseValue(database, firstField, entry);
            loadDependencies(
              {
                contentId: database.contentId,
                field: firstField,
                entryId: entry.id,
                value,
              },
              {
                ...helpers,
                selectDatabase: getSelectDatabaseDraft(true),
                intl,
              },
              store.getState()
            );
          }
        }
      },
      [database, intl, store]
    )
  );

  const handleChange = useCallback(
    (val: ReadonlyArray<EntryWithContext> | EntryWithContext | null) => {
      const arr = Array.isArray(val) ? val : val ? [val] : [];
      onChange(arr.map((v) => v.entry.id));
    },
    [onChange]
  );

  const resolvedOptions = useMemo(
    (): Array<EntryWithContext> => [
      ...((database &&
        getDatabaseEntries(database).map((entry) => ({ database, entry }))) ??
        []),
    ],
    [database]
  );

  const resolvedValue = useMemo(
    () =>
      database
        ? value.flatMap((v): Array<EntryWithContext> => {
            if (database.entriesById[v]) {
              const entry = database.entriesById[v];
              return [{ database, entry }];
            } else {
              return [];
            }
          })
        : [],
    [database, value]
  );

  const noOptionsMessageResolved = useMemo(
    () => (noOptionsMessage ? () => noOptionsMessage : undefined),
    [noOptionsMessage]
  );

  const getOptionLabel = useCallback(
    (value: EntryWithContext) => {
      const entryName = getEntryName(value, store.getState());

      return entryName
        ? entryName
        : formatMessage(messages['unnamed-entities.unnamed-entry']);
    },
    [formatMessage, store]
  );

  const formatOptionLabel = useCallback(
    (
      value: EntryWithContext,
      meta: FormatOptionLabelMeta<EntryWithContext, boolean>
    ): React.ReactNode => {
      if (meta.context === 'menu') {
        return (
          <OptionContainer>
            {database && getCoverField(database) && (
              <EntryCoverImage
                contentId={value.database.contentId}
                entryId={value.entry.id}
                width={38}
              />
            )}
            {getOptionLabel(value)}
          </OptionContainer>
        );
      } else if (isLinkPreview) {
        if (value.entry) {
          const entryRef = {
            databaseSpaceKey: value.database.space.key,
            databaseId: value.database.id,
            entryId: value.entry.id,
            title: getOptionLabel(value),
          };
          return <EntryLink entryRef={entryRef} noFocus />;
        } else {
          return <BrokenLink />;
        }
      } else {
        return getOptionLabel(value);
      }
    },
    [database, getOptionLabel, isLinkPreview]
  );

  return (
    <ValidationStatus
      error={validateEntry(
        resolvedValue,
        isRequired,
        formatMessage(messages['entry-picker.validate-error-text'])
      )}
    >
      {(validationProps) => (
        <Select<EntryWithContext, boolean>
          classNamePrefix="entry-picker"
          value={resolvedValue}
          onChange={handleChange}
          isClearable={isClearable}
          isDisabled={isDisabled ?? !database}
          isInvalid={validationProps.isInvalid || isInvalid}
          formatOptionLabel={formatOptionLabel}
          getOptionLabel={getOptionLabel}
          options={resolvedOptions}
          getOptionValue={getOptionValue}
          onFocus={onFocus}
          onBlur={onBlur}
          autoFocus={autoFocus}
          isLoading={isLoading || dependencies.isDispatching}
          noOptionsMessage={noOptionsMessageResolved}
          styles={styles}
          placeholder={formatMessage(messages['select-dropdown.placeholder'])}
          isMulti={isMulti}
          isSeamless={isSeamless}
          defaultMenuIsOpen={autoFocus && !resolvedValue.length}
        />
      )}
    </ValidationStatus>
  );
}

export interface EntryPickerProps
  extends Omit<MultiEntryPickerProps, 'value' | 'onChange' | 'isMulti'> {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}

/**
 * Renders a select box that allows selecting a single specific database entry from the given database.
 */
export default function EntryPicker({
  value,
  onChange,
  ...props
}: EntryPickerProps): ReactElement {
  const resolvedValue = useMemo(() => (value ? [value] : []), [value]);
  const handleChange = useCallback(
    (val: Array<string>) => {
      onChange(val[0]);
    },
    [onChange]
  );

  return <MultiEntryPicker {...props} value={resolvedValue} onChange={handleChange} />;
}
